import type { EffortLevel, Service } from "../types";

export type ServicePricingInput = Pick<
  Service,
  "price" | "priceLight" | "priceMedium" | "priceHeavy"
>;

/** True when the service defines at least one effort-specific price (Leve / Médio / Pesado). */
export function serviceHasEffortTierPrices(
  service: ServicePricingInput | null | undefined
): boolean {
  if (!service) return false;
  const isSet = (v: number | null | undefined) =>
    v != null && !Number.isNaN(Number(v));
  return (
    isSet(service.priceLight) ||
    isSet(service.priceMedium) ||
    isSet(service.priceHeavy)
  );
}

function parsePriceNumber(value: string | number | null | undefined): number {
  if (value == null || value === "") return 0;
  const n =
    typeof value === "number" ? value : parseFloat(String(value).replace(",", "."));
  return Number.isNaN(n) ? 0 : n;
}

function formatCompactPrice(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.?0+$/, "");
}

/**
 * Price fragment for service select options.
 * When 2+ effort tiers exist: `R$ 100/150/200` (Leve/Médio/Pesado that are set).
 * Otherwise: base service price as `R$ 100.00`.
 */
export function formatServiceSelectPrice(
  service: ServicePricingInput | null | undefined
): string {
  if (!service) return "R$ 0";

  const effortPrices = [
    service.priceLight,
    service.priceMedium,
    service.priceHeavy,
  ]
    .filter((v): v is number => v != null && !Number.isNaN(Number(v)))
    .map((v) => formatCompactPrice(Number(v)));

  if (effortPrices.length > 1) {
    return `R$ ${effortPrices.join("/")}`;
  }

  return `R$ ${formatCompactPrice(parsePriceNumber(service.price))}`;
}

/**
 * Full select option label: `Service name - R$ 100/150/200` (or base price).
 */
export function formatServiceSelectLabel(
  service: (ServicePricingInput & { name: string }) | null | undefined
): string {
  if (!service) return "";
  return `${service.name} - ${formatServiceSelectPrice(service)}`;
}

/**
 * Appointment/service price for a car: tier matching effort when set, otherwise base `service.price`.
 */
export function resolvePriceForCarEffort(
  service: ServicePricingInput | null | undefined,
  effort: EffortLevel | string | null | undefined
): string {
  const base = parsePriceNumber(service?.price);
  if (!service) return base.toFixed(2);

  if (effort == null || effort === "") {
    return base.toFixed(2);
  }

  let tier: number | null | undefined;
  if (effort === "Leve") tier = service.priceLight ?? null;
  else if (effort === "Médio") tier = service.priceMedium ?? null;
  else if (effort === "Pesado") tier = service.priceHeavy ?? null;

  if (tier != null && !Number.isNaN(Number(tier))) {
    return Number(tier).toFixed(2);
  }

  return base.toFixed(2);
}
